import { Typography, FormControl, FormControlLabel, Radio, RadioGroup } from '@tunasong/ui-lib'
import { Poll as PollIcon } from '@tunasong/icons'
import { isOneOfManyPoll, isOneOfManyPollResponse } from '@tunasong/schemas'
import type { OneOfManyPollResponse } from '@tunasong/schemas'
import { useCallback } from 'react'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import type { PollProps } from '../poll.js'
import { getProgress } from '../progress-util.js'
import Progress from '../progress.js'
import { PollOptionName } from '../poll-option-name.js'
import { PollAudioProvider, WithOptionAudio } from '../option-audio.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import classNames from 'classnames'
import type { HTMLAttributes } from 'react'
import styles from './final.module.css'

const PollContainer = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={classNames(styles.container, className)} {...props} />
)

export const OneOfManyPollFinal: FC<PollProps> = props => {
  const { poll, userId, onChange } = props

  invariant(isOneOfManyPoll(poll), `Poll must be a OneOfManyPoll`)

  const { entities: responses = [], isSuccess } = graphHooks.useEntityChildren<OneOfManyPollResponse>({
    parentId: poll.id,
    filter: isOneOfManyPollResponse,
  })
  const userResponse = responses.find(response => response.userId === userId)

  const { isCreatingEntity, hasCreatedEntity } = graphHooks.useEntityGraph()

  const hasVoted = isSuccess && Boolean(userResponse)

  const { name = 'Untitled', pollOptions = [], finalOption, closed } = poll
  const readOnly = isCreatingEntity || !isSuccess || hasCreatedEntity || closed

  const handleChange = useCallback(
    async (ev: unknown, val: string) => {
      if (!(userId && onChange)) {
        throw new Error(`userId must be set`)
      }
      const choice = parseInt(val, 10)

      onChange({ ...poll, finalOption: choice })
    },
    [onChange, poll, userId]
  )

  const response = finalOption
  const numResponses = Object.keys(responses).length

  return (
    <PollAudioProvider pollId={poll.id}>
      <PollContainer>
        <Typography className={styles.title} variant="subtitle1">
          <PollIcon /> {name}
        </Typography>
        {poll.closed && (
          <Typography variant="caption" color="secondary">
            The Poll is closed.
          </Typography>
        )}
        <Typography variant="caption">{numResponses} participants.</Typography>

        <FormControl className={styles.formControl}>
          <RadioGroup aria-label="options" name="options" value={response ?? ''} onChange={handleChange}>
            {pollOptions.map((option, idx) => (
              <WithOptionAudio key={idx} option={option}>
                <FormControlLabel
                  className={styles.option}
                  disabled={readOnly}
                  value={idx}
                  control={<Radio />}
                  label={
                    <>
                      <PollOptionName option={option} withAudio={false} />
                      {hasVoted ? (
                        <Progress value={getProgress(poll, responses, idx)} selected={idx === finalOption} />
                      ) : null}
                    </>
                  }
                />
              </WithOptionAudio>
            ))}
          </RadioGroup>
        </FormControl>
      </PollContainer>
    </PollAudioProvider>
  )
}
